import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import simplify from "simplify-js";
import { drawStartingPoint } from "~/components/Annotations/components/canvasUtils";
import { AnnotationCommentInput } from "~/components/Annotations/shared/AnnotationCommentInput";
import { getAnnotationCommentPos, getPageNumber } from "~/components/Annotations/shared/annotationUtils";
import { DrawingCanvas } from "~/components/BoundingBox/components/DrawingCanvas";
export const PencilAnnotationLayer = /*#__PURE__*/ memo((param)=>{
    let { containerRef, disabled, containerWidth, containerHeight, containerLeft, containerTop, onIsSelectingChange, onAnnotationDrawn, pageNumber, newAnnotation, lineColor, lineWidth, activeAnnotation, onNewLineDrawStart } = param;
    const annotationLayerRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const paths = useRef([]);
    const annotationToDraw = newAnnotation || activeAnnotation;
    const addPointToPath = useCallback((point)=>{
        const lastIndex = paths.current.length;
        paths.current[lastIndex - 1].push(point);
    }, []);
    // This will draw a rectangle on the canvas based on user input.
    const onMouseMove = useCallback((event)=>{
        var _annotationLayerRef_current_getCanvas, _annotationLayerRef_current, _annotationLayerRef_current1;
        const ctx = (_annotationLayerRef_current = annotationLayerRef.current) === null || _annotationLayerRef_current === void 0 ? void 0 : (_annotationLayerRef_current_getCanvas = _annotationLayerRef_current.getCanvas()) === null || _annotationLayerRef_current_getCanvas === void 0 ? void 0 : _annotationLayerRef_current_getCanvas.getContext("2d");
        const mouseCoords = (_annotationLayerRef_current1 = annotationLayerRef.current) === null || _annotationLayerRef_current1 === void 0 ? void 0 : _annotationLayerRef_current1.getMouseCoords({
            event
        });
        if (ctx && mouseCoords) {
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = lineWidth;
            ctx.lineTo(mouseCoords.x, mouseCoords.y);
            ctx.stroke();
            addPointToPath(mouseCoords);
        }
    }, [
        addPointToPath,
        lineColor,
        lineWidth
    ]);
    // This will set the drawing in context, as well as generate and set percentage
    // values for each corner of the rect.
    const onMouseUp = useCallback(()=>{
        setIsDrawing(false);
        const lastIndex = paths.current.length;
        paths.current[lastIndex - 1] = simplify(paths.current[lastIndex - 1], 1, false);
        if (containerWidth && containerHeight) {
            paths.current[lastIndex - 1] = simplify(paths.current[lastIndex - 1], 1, false).map((param)=>{
                let { x, y } = param;
                return {
                    x: x / containerWidth,
                    y: y / containerHeight
                };
            });
            onAnnotationDrawn({
                type: "pencil",
                lines: paths.current.map((path)=>({
                        coordinates: path,
                        pageNumber,
                        color: lineColor,
                        lineWidth: lineWidth
                    }))
            });
        }
        onIsSelectingChange === null || onIsSelectingChange === void 0 ? void 0 : onIsSelectingChange(false);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
    }, [
        containerHeight,
        containerWidth,
        lineColor,
        lineWidth,
        onAnnotationDrawn,
        onIsSelectingChange,
        onMouseMove,
        pageNumber
    ]);
    const redrawPaths = useCallback(()=>{
        var _annotationLayerRef_current_getCanvas, _annotationLayerRef_current;
        const ctx = (_annotationLayerRef_current = annotationLayerRef.current) === null || _annotationLayerRef_current === void 0 ? void 0 : (_annotationLayerRef_current_getCanvas = _annotationLayerRef_current.getCanvas()) === null || _annotationLayerRef_current_getCanvas === void 0 ? void 0 : _annotationLayerRef_current_getCanvas.getContext("2d");
        if (ctx && annotationToDraw) {
            var _annotationLayerRef_current1;
            (_annotationLayerRef_current1 = annotationLayerRef.current) === null || _annotationLayerRef_current1 === void 0 ? void 0 : _annotationLayerRef_current1.clearCanvas();
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = lineWidth;
            annotationToDraw.lines.forEach((path)=>{
                ctx.beginPath();
                path.coordinates.forEach((param, index)=>{
                    let { x, y } = param;
                    const containerX = x * containerWidth;
                    const containerY = y * containerHeight;
                    if (index === 0) {
                        drawStartingPoint({
                            point: {
                                x: containerX,
                                y: containerY
                            },
                            ctx,
                            lineSize: lineWidth / 2,
                            lineColor
                        });
                    } else {
                        ctx.lineTo(containerX, containerY);
                        ctx.stroke();
                    }
                });
            });
        }
    }, [
        containerHeight,
        containerWidth,
        lineColor,
        lineWidth,
        annotationToDraw
    ]);
    const onMouseDown = useCallback((event)=>{
        var _annotationLayerRef_current_getCanvas, _annotationLayerRef_current, _annotationLayerRef_current1;
        event.stopPropagation();
        if (activeAnnotation) {
            onNewLineDrawStart === null || onNewLineDrawStart === void 0 ? void 0 : onNewLineDrawStart(event);
            return;
        } else {
            paths.current.push([]);
        }
        const ctx = (_annotationLayerRef_current = annotationLayerRef.current) === null || _annotationLayerRef_current === void 0 ? void 0 : (_annotationLayerRef_current_getCanvas = _annotationLayerRef_current.getCanvas()) === null || _annotationLayerRef_current_getCanvas === void 0 ? void 0 : _annotationLayerRef_current_getCanvas.getContext("2d");
        const mouseCoords = (_annotationLayerRef_current1 = annotationLayerRef.current) === null || _annotationLayerRef_current1 === void 0 ? void 0 : _annotationLayerRef_current1.getMouseCoords({
            event
        });
        if (ctx && mouseCoords) {
            setIsDrawing(true);
            drawStartingPoint({
                point: mouseCoords,
                ctx,
                lineSize: lineWidth / 2,
                lineColor
            });
            addPointToPath(mouseCoords);
            onIsSelectingChange === null || onIsSelectingChange === void 0 ? void 0 : onIsSelectingChange(true);
            window.addEventListener("mousemove", onMouseMove);
            window.addEventListener("mouseup", onMouseUp);
        }
    }, [
        activeAnnotation,
        addPointToPath,
        lineColor,
        lineWidth,
        onIsSelectingChange,
        onMouseMove,
        onMouseUp,
        onNewLineDrawStart
    ]);
    useEffect(()=>{
        redrawPaths();
    // omit redrawPaths - we want to call it when color or lineWidth changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        lineColor,
        lineWidth
    ]);
    useEffect(()=>{
        if (!annotationToDraw) {
            var _annotationLayerRef_current;
            paths.current = [];
            (_annotationLayerRef_current = annotationLayerRef.current) === null || _annotationLayerRef_current === void 0 ? void 0 : _annotationLayerRef_current.clearCanvas();
        } else {
            const annotationLines = annotationToDraw.lines.map((line)=>line.coordinates);
            if (!isEqual(paths.current, annotationLines)) {
                var _annotationLayerRef_current1;
                paths.current = annotationToDraw.lines.map((line)=>line.coordinates);
                (_annotationLayerRef_current1 = annotationLayerRef.current) === null || _annotationLayerRef_current1 === void 0 ? void 0 : _annotationLayerRef_current1.clearCanvas();
                redrawPaths();
            }
        }
    }, [
        annotationToDraw,
        redrawPaths
    ]);
    const { maxY, maxX, minX } = useMemo(()=>annotationToDraw ? getAnnotationCommentPos(annotationToDraw) : {
            maxX: 0,
            maxY: 0,
            minX: 0
        }, [
        annotationToDraw
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(DrawingCanvas, {
                hasCustomCursor: true,
                containerWidth: containerWidth,
                containerHeight: containerHeight,
                onMouseDown: disabled ? undefined : onMouseDown,
                ref: annotationLayerRef,
                onSizeChanged: redrawPaths,
                containerLeft: containerLeft,
                containerTop: containerTop
            }),
            !isDrawing && newAnnotation && getPageNumber(newAnnotation) === pageNumber && newAnnotation.lines.length > 0 && /*#__PURE__*/ _jsx(AnnotationCommentInput, {
                containerRef: containerRef,
                containerWidth: containerWidth,
                containerHeight: containerHeight,
                startX: minX,
                endX: maxX,
                endY: maxY,
                containerLeft: containerLeft
            })
        ]
    });
});
PencilAnnotationLayer.displayName = "PenAnnotationLayer";
